import { Loader, RedirectError } from 'logard';
import { reactive } from 'vue';
import ChatService from '../chat/services/ChatService';
import ChecklistClient from '../checklist/client/ChecklistClient';
import Checklist, { ChecklistId } from '../checklist/models/Checklist';
import { DOSSIER_DETAIL_ROUTE, HOME_ROUTE } from '../general/router/constants';
import AuthService from '../general/services/AuthService';
import MediaFileService from '../general/services/MediaFileService';
import { validateChatType } from '../general/utils/chatHelpers';
import { JobId } from '../jobs/models/AbstractJob';
import JobService from '../jobs/services/JobService';
import Permission from '../shared/constants/Permission';
import { ContextLoader } from '../shared/models/AbstractContext';
import { mergeInto } from '../shared/utils';
import { saneNumericId } from '../shared/utils/sanitizers';
import { isPresent } from '../shared/utils/typescript';
import { DossierId } from './models/AbstractDossier';
import DossierService from './services/DossierService';
import CheckInContext from '@/dossier/models/CheckInContext';
import CheckInService from '@/dossier/services/CheckInService';
import DiagnosticClient from '@/jobs/client/DiagnosticClient';
import DriveBookingService from '@/driveBookings/services/DriveBookingService';

export const dossierLoader = new ContextLoader(async (s) => {
  const id = s.getPathParam('dossierId', saneNumericId<DossierId>);
  if (!isPresent(id)) throw new RedirectError({ name: HOME_ROUTE });
  return DossierService.load(id);
});

export const plannerDossierLoader = new ContextLoader(async s => {
  const id = s.getPathParam('dossierId', saneNumericId<DossierId>);
  if (isPresent(id)) {
    return DossierService.loadForPlanner(id);
  }

  const queryParamId = s.getQueryParam('dossierId', saneNumericId<DossierId>);
  if (isPresent(queryParamId)) {
    return DossierService.loadForPlanner(queryParamId);
  }
});

export const checkInLoader = new Loader(async s => {
  const dossierContext =  await dossierLoader.getResult(s);
  await DriveBookingService.loadDriveBookings(dossierContext.dossier.id, dossierContext.driveBookingContext);
  dossierContext.jobContext.diagnosticTypes = await DiagnosticClient.loadDiagnosticTypes(dossierContext.dossier.id);
  const checkInDetails = await CheckInService.getDetails(dossierContext);
  const context = new CheckInContext(dossierContext, checkInDetails);
  JobService.startGeneralListeners(context.editableJobContext, dossierContext.jobContext); // Attach listeners to editableJobContext
  return reactive(context);
});

export const chatLoader = new ContextLoader(async s => {
  const currentUser = AuthService.getCurrentUser();
  if (currentUser.hasPermission(Permission.RESTRICTED)) return undefined;
  const chatType = validateChatType(s.getQueryParam('chat'));
  const dossierContext = await dossierLoader.getResult(s);
  return ChatService.loadForDossier(
    dossierContext.dossier,
    dossierContext.contactContext,
    chatType,
    currentUser,
  );
});

export const dossierDetailLoader = new ContextLoader(async (s) => {
  const dossierContext = await dossierLoader.getResult(s);
  return DossierService.loadDetail(dossierContext);
});

export const jobPageLoader = new ContextLoader(async (s) => {
  const dossierContext = await dossierLoader.getResult(s);
  return JobService.loadMaster(dossierContext);
});

export const jobDetailLoader = new Loader(async (s) => {
  const context = await jobPageLoader.getResult(s);
  const id = s.getPathParam('jobId', saneNumericId<JobId>) ?? null;
  await JobService.loadDetail(id, context);
  return context;
});

export const jobMediaLoader = new Loader(async (s) => {
  const jobContext = await jobDetailLoader.getResult(s);
  return MediaFileService.loadForJob(jobContext);
});

export const checklistLoader = new Loader(async (s) => {
  const context = await dossierDetailLoader.getResult(s);
  const id = s.getPathParam('checklistId', saneNumericId<ChecklistId>);
  if (!isPresent(id)) throw new RedirectError({ name: DOSSIER_DETAIL_ROUTE });
  const checklist = reactive(
    await ChecklistClient.loadDetail(id, context.dossier.id),
  ) as Checklist;
  checklist.recalculateCounters();
  mergeInto(checklist, context.checklists);
  return checklist;
});
